/**
 * Mengubah string warna hex menjadi objek RGB.
 * Mendukung format 3-digit (#F0C) dan 6-digit (#FF00CC).
 * @param hex - String warna hex.
 * @returns Objek {r, g, b} atau null jika format tidak valid.
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    // Mengubah format singkat (e.g. "03F") menjadi format penuh (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (_m, r, g, b) => r + r + g + g + b + b);

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);

    if (!result) {
        return null;
    }

    return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    };
};

/**
 * Mengubah nilai RGB menjadi string warna hex.
 * @param r - Nilai Merah (0-255).
 * @param g - Nilai Hijau (0-255).
 * @param b - Nilai Biru (0-255).
 * @returns String warna hex, contoh: "#ff00cc".
 */
const rgbToHex = (r: number, g: number, b: number): string => {
    const toHex = (c: number): string => {
        // Bulatkan nilai dan ubah ke basis 16 (hex)
        const hex = Math.round(c).toString(16);
        // Tambahkan '0' di depan jika hanya satu digit
        return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Mencampurkan dua warna hex dengan tingkat opasitas tertentu (Alpha Blending).
 * @param foregroundColor - Warna di lapisan atas (misal: #ec336f).
 * @param backgroundColor - Warna di lapisan bawah (misal: #ffffff).
 * @param opacity - Tingkat opasitas warna depan (nilai antara 0.0 sampai 1.0).
 * @returns String warna hex hasil campuran, atau null jika ada format yang salah.
 */
export const blendColors = (
    foregroundColor: string,
    backgroundColor: string,
    opacity: number
): string | null => {
    // Pastikan opasitas berada dalam rentang yang valid
    const validOpacity = Math.max(0, Math.min(1, opacity));

    const fgRgb = hexToRgb(foregroundColor);
    const bgRgb = hexToRgb(backgroundColor);

    if (!fgRgb || !bgRgb) {
        console.error("Format warna hex tidak valid.");
        return null;
    }

    // Terapkan formula alpha blending untuk setiap channel
    const r = (fgRgb.r * validOpacity) + (bgRgb.r * (1 - validOpacity));
    const g = (fgRgb.g * validOpacity) + (bgRgb.g * (1 - validOpacity));
    const b = (fgRgb.b * validOpacity) + (bgRgb.b * (1 - validOpacity));

    return rgbToHex(r, g, b);
};

/**
 * Mengambil nilai dari sebuah CSS Custom Property (Variable).
 * @param {string} variableName - Nama variabel (contoh: '--tblr-primary').
 * @param {Element} element - Elemen referensi (default: document.documentElement).
 * @returns {string} Nilai dari variabel tersebut.
 */
export function getCssVariable(variableName: string, element: Element = document.documentElement): string {
    const styles = getComputedStyle(element);
    return styles.getPropertyValue(variableName).trim();
}